// In production, this will be the full URL of the Azure Function App
export const API_BASE_URL = '/api'; // Force Unified Server Path (v2.2 Fix)

export const getAuthToken = () => localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
export const setAuthToken = (token: string) => localStorage.setItem('xc_auth_token', token);
export const removeAuthToken = () => localStorage.removeItem('xc_auth_token');

export const getCurrentUser = () => {
    const u = localStorage.getItem('xc_user');
    return u ? JSON.parse(u) : null;
};

const handleResponse = async (res: Response, defaultError: string) => {
    if (res.ok) return res.json();
    if (res.status === 401) throw new Error("Nicht eingeloggt. Bitte anmelden.");
    const text = await res.text();
    try {
        const json = JSON.parse(text);
        throw new Error(json.error || json.message || text || defaultError);
    } catch (e) {
        throw new Error(text || defaultError);
    }
};

export const apiRequest = async (endpoint: string, method = 'GET', body?: any) => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json'
    };

    const token = getAuthToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined
    });

    // NICHT automatisch Token löschen bei 401!
    // Das verursacht Race-Conditions: wenn ein Settings-Call 401 bekommt,
    // wird der Token gelöscht und alle parallelen Calls schlagen fehl.
    // Token-Lifecycle wird durch Login/Logout & Profil-Refresh verwaltet.
    if (res.status === 401) {
        console.warn(`[API] 401 on ${endpoint} – Token möglicherweise abgelaufen`);
        if (typeof window !== 'undefined' && !endpoint.includes('/auth/')) {
            window.dispatchEvent(new CustomEvent('auth-expired'));
        }
    }

    return res;
};

export const ApiService = {
    // Auth
    login: async (email: string, password: string) => {
        const res = await apiRequest('/auth/login', 'POST', { email, password });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.message || err.error || 'Login fehlgeschlagen');
        }
        const data = await res.json();
        setAuthToken(data.token);
        localStorage.setItem('xc_user', JSON.stringify(data.user));
        return data.user;
    },

    register: async (data: any) => {
        const res = await apiRequest('/auth/register', 'POST', data);
        if (!res.ok) {
            const text = await res.text();
            let err: any = {};
            try { err = JSON.parse(text); } catch(e) {}
            throw new Error(err.error || err.message || text || 'Registrierung fehlgeschlagen');
        }
        const responseData = await res.json();
        if (responseData.token) {
            setAuthToken(responseData.token);
            localStorage.setItem('xc_user', JSON.stringify(responseData.user));
        }
        return responseData.user || responseData;
    },

    verifyEmail: async (token: string) => {
        const res = await apiRequest(`/auth/verify?token=${token}`, 'GET');
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || err.message || 'Verifizierung fehlgeschlagen');
        }
        return await res.json();
    },

    requestPasswordReset: async (email: string) => {
        const res = await apiRequest('/auth/reset/request', 'POST', { email });
        // Always success for security reasons (unless 500)
        if (!res.ok) throw new Error('Reset request failed');
        return await res.json();
    },

    confirmPasswordReset: async (data: any) => {
        const res = await apiRequest('/auth/reset/confirm', 'POST', data);
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || 'Reset failed');
        }
        return await res.json();
    },

    getTakeoffs: async () => {
        try {
            const res = await apiRequest('/takeoffs');
            if (res.ok) return await res.json();
            throw new Error(res.statusText);
        } catch (e) {
            console.warn("Backend unavailable", e);
            return [];
        }
    },

    getAirspaces: async (bounds: any) => {
        const { minLat, maxLat, minLng, maxLng } = bounds;
        try {
            const res = await apiRequest(`/airspaces?minLat=${minLat}&maxLat=${maxLat}&minLng=${minLng}&maxLng=${maxLng}`);
            if (res.ok) return await res.json();
            console.error("Airspace Fetch Failed:", res.status, await res.text());
            throw new Error(res.statusText);
        } catch (e) {
            console.error("Backend unavailable (Airspaces)", e);
            return { type: "FeatureCollection", features: [] };
        }
    },

    migrateTakeoffs: async () => {
        const res = await apiRequest('/migrate-takeoffs');
        if (!res.ok) throw new Error('Migration failed');
        return await res.text();
    },

    saveTakeoff: async (takeoff: any) => {
        const res = await apiRequest('/takeoffs', 'POST', takeoff);
        return handleResponse(res, 'Failed to save takeoff');
    },

    deleteTakeoff: async (id: string) => {
        const res = await apiRequest(`/takeoffs?id=${id}`, 'DELETE');
        return handleResponse(res, 'Failed to delete takeoff');
    },

    getMapElements: async () => {
        try {
            const res = await apiRequest('/map-elements');
            if (res.ok) return await res.json();
            throw new Error(res.statusText);
        } catch (e) {
            console.warn("Backend unavailable", e);
            return [];
        }
    },

    saveMapElement: async (el: any) => {
        const res = await apiRequest('/map-elements', 'POST', el);
        return handleResponse(res, 'Failed to save map element');
    },

    deleteMapElement: async (id: string) => {
        const res = await apiRequest(`/map-elements?id=${id}`, 'DELETE');
        return handleResponse(res, 'Failed to delete map element');
    },

    getMapSettings: async () => {
        try {
            const res = await apiRequest('/user/settings');
            if (res.ok) return await res.json();
            console.warn("Settings failed:", res.status, res.statusText);
            return null; // Return null on failure to prevent overwrite
        } catch (e) {
            console.warn("Settings load failed", e);
            return null;
        }
    },

    saveMapSettings: async (settings: any) => {
        const res = await apiRequest('/user/settings', 'POST', settings);
        if (!res.ok) throw new Error('Failed to save settings');
        return await res.json();
    },

    // System Settings (Global Config)
    getSystemSettings: async () => {
        const res = await apiRequest('/system/settings');
        if (!res.ok) throw new Error('Failed to load settings');
        return await res.json(); // Returns object { key: value }
    },

    saveSystemSetting: async (key: string, value: string) => {
        const res = await apiRequest('/system/settings', 'POST', { key, value });
        return handleResponse(res, 'Failed to save setting');
    },

    // Pricing Config (MwSt & Wechselkurs)
    getPricingConfig: async () => {
        try {
            const res = await apiRequest('/pricing/config');
            if (res.ok) return await res.json();
            throw new Error(res.statusText);
        } catch (e) {
            console.warn("Pricing config unavailable, using defaults", e);
            return { vat_rate_ch: 8.1, vat_rate_de: 19, vat_rate_at: 20, exchange_rate_eur_chf: 0.93 };
        }
    },



    getProfile: async () => {
        const res = await apiRequest('/auth/profile');
        if (!res.ok) throw new Error('Failed to load profile');
        return await res.json();
    },

    updateProfile: async (data: any) => {
        const res = await apiRequest('/auth/profile', 'PUT', data);
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || 'Failed to update profile');
        }
        const json = await res.json();
        if (json.user) {
            const current = JSON.parse(localStorage.getItem('xc_user') || '{}');
            localStorage.setItem('xc_user', JSON.stringify({ ...current, ...json.user }));
        }
        return json;
    },

    listUsers: async () => {
        const res = await apiRequest('/management/users');
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || 'Benutzerliste konnte nicht geladen werden');
        }
        return await res.json();
    },

    getSubscriptionPlans: async () => {
        try {
            const res = await apiRequest('/subscription-plans');
            if (res.ok) return await res.json();
            throw new Error(res.statusText);
        } catch (e) {
            console.warn("Backend unavailable", e);
            return [];
        }
    },

    saveSubscriptionPlan: async (plan: any) => {
        const method = plan.id ? 'PUT' : 'POST';
        const res = await apiRequest('/subscription-plans', method, plan);
        if (!res.ok) {
            let detail = '';
            try { const body = await res.json(); detail = body.error || body.details || body.message || ''; } catch { }
            throw new Error(detail || `Failed to save plan (${res.status})`);
        }
        return await res.json();
    },

    deleteSubscriptionPlan: async (id: string) => {
        const res = await apiRequest(`/subscription-plans?id=${id}`, 'DELETE');
        if (!res.ok) throw new Error('Failed to delete plan');
        return await res.json();
    },

    // Token Packages
    getTokenPackages: async () => {
        try {
            const res = await apiRequest('/token-packages'); // Endpoint matches function route
            if (res.ok) return await res.json();
            throw new Error(res.statusText);
        } catch (e) {
            console.warn("Backend unavailable", e);
            return [];
        }
    },

    saveTokenPackage: async (pkg: any) => {
        const method = pkg.id ? 'PUT' : 'POST';
        const res = await apiRequest('/token-packages', method, pkg);
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || err.message || res.statusText || 'Failed to save package');
        }
        return await res.json();
    },

    deleteTokenPackage: async (id: string) => {
        const res = await apiRequest(`/token-packages?id=${id}`, 'DELETE');
        if (!res.ok) throw new Error('Failed to delete package');
        return await res.json();
    },

    purchaseTokenPackage: async (packageId: string) => {
        // Legacy or Mock - switching to Payrexx below
        const res = await apiRequest('/token/purchase', 'POST', { packageId });
        if (!res.ok) throw new Error('Purchase failed');
        return await res.json();
    },

    initPayment: async (itemId: string, type: 'plan' | 'package' | 'ad', intent?: 'extend' | 'replace') => {
        const payload = type === 'plan' ? { planId: itemId, intent } : type === 'package' ? { packageId: itemId } : { adId: itemId };
        const res = await apiRequest('/payment/init', 'POST', payload);
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.details || err.error || 'Payment initialization failed');
        }
        return await res.json();
    },

    verifyPayment: async (txId: string) => {
        const res = await apiRequest(`/payment/verify?txId=${txId}`);
        if (!res.ok) throw new Error('Verification failed');
        return await res.json();
    },

    // Admin User & Subscription Management
    adminCreateUser: async (user: any) => {
        const res = await apiRequest('/management/users', 'POST', user);
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || 'Failed to create user');
        }
        return await res.json();
    },

    adminAssignSubscription: async (userId: string, planId: string) => {
        const res = await apiRequest('/backoffice/assign-subscription', 'POST', { userId, planId });
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || 'Failed to assign subscription');
        }
        return await res.json();
    },

    adminUpdateUser: async (userId: string, data: any) => {
        const res = await apiRequest(`/management/users/${userId}`, 'PUT', data);
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || 'Failed to update user');
        }
        const json = await res.json();
        if (json.user) {
            const current = JSON.parse(localStorage.getItem('xc_user') || '{}');
            if (current.id === userId) {
                localStorage.setItem('xc_user', JSON.stringify({ ...current, ...json.user }));
            }
        }
        return json;
    },

    adminAddTokens: async (userId: string, amount: number, type: 'purchased' | 'subscription' = 'purchased') => {
        const payload = type === 'subscription' ? { tokens_subscription_add: amount } : { tokens_purchased_add: amount };
        const res = await apiRequest(`/management/users/${userId}`, 'PUT', payload);
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || 'Failed to add tokens');
        }
        return await res.json();
    },

    adminDeleteUser: async (userId: string) => {
        const res = await apiRequest(`/management/users/${userId}`, 'DELETE');
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || 'Failed to delete user');
        }
        return await res.json();
    },

    // System Lists (Dynamic Data)
    getSystemLists: async (category?: string) => {
        const query = category ? `?category=${category}` : '';
        const res = await apiRequest(`/v1/system-lists${query}`);
        if (!res.ok) throw new Error('Failed to load system lists');
        return await res.json();
    },

    saveSystemList: async (key: string, value: any, category?: string, label?: string) => {
        const res = await apiRequest('/v1/system-lists', 'POST', { key, value, category, label });
        if (!res.ok) throw new Error('Failed to save system list');
        return await res.json();
    },

    savePilotProfile: async (profile: any) => {
        const res = await apiRequest('/users/me/pilot-profile', 'POST', profile);
        if (!res.ok) throw new Error('Failed to save pilot profile');
        return await res.json();
    },

    getBuddyProfile: async () => {
        const res = await apiRequest('/users/me/buddy-profile', 'GET');
        if (!res.ok) {
            const err = await res.text();
            throw new Error(`Failed to load buddy profile: ${err}`);
        }
        return await res.json();
    },

    saveBuddyProfile: async (profile: any) => {
        const res = await apiRequest('/users/me/buddy-profile', 'POST', profile);
        if (!res.ok) {
            const err = await res.text();
            throw new Error(`Failed to save buddy profile: ${err}`);
        }
        return await res.json();
    },

    runRiskAnalysis: async () => {
        const res = await apiRequest('/ai/risk-analysis', 'POST', {});
        if (!res.ok) {
            const text = await res.text(); // Read once
            let err: any = {};
            try {
                err = JSON.parse(text);
                console.error("[API] Risk Analysis Error JSON:", err);
            } catch (e) {
                console.error("[API] Risk Analysis Error TEXT:", text);
                err = { message: text };
            }

            // Pass specific error details strictly
            const msg = err.message || err.error || 'Analysis failed (500)';
            // Use specific error object if needed or just string
            const error: any = new Error(msg);
            if (err.currentBalance !== undefined) error.currentBalance = err.currentBalance;
            if (err.required !== undefined) error.required = err.required;
            if (err.stack) console.error("[API] Stack:", err.stack);
            throw error;
        }
        return await res.json();
    },

    invokeAdminAction: async (action: string, payload?: any) => {
        const res = await apiRequest(`/admin/${action}`, 'POST', payload);
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.message || res.statusText || 'Action failed');
        }
        return await res.json();
    },

    // Marketplace
    getMarketplaceAds: async (category?: string, search?: string) => {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (search) params.append('search', search);

        const res = await apiRequest(`/market/ads?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to load ads');
        return await res.json();
    },

    getMyAds: async () => {
        const res = await apiRequest('/market/my-ads');
        if (!res.ok) throw new Error('Failed to load my ads');
        return await res.json();
    },

    manageAd: async (ad: any) => {
        const res = await apiRequest('/market/ads', 'POST', ad);
        if (!res.ok) {
            let backendError = 'Failed to save ad';
            try {
                const data = await res.json();
                if (data.error) backendError = data.error;
                if (data.error === "AI_BLOCKED") throw new Error(`AI_BLOCKED:${data.reason}`);
            } catch (e: any) {
                if (e.message?.startsWith('AI_BLOCKED:')) throw e;
            }
            throw new Error(backendError);
        }
        return await res.json();
    },

    deleteAd: async (id: string) => {
        const res = await apiRequest(`/market/ads?id=${id}`, 'DELETE');
        if (!res.ok) throw new Error('Failed to delete ad');
        return await res.json();
    },

    // Messages
    sendMessage: async (receiverId: string, subject: string, body: string) => {
        const res = await apiRequest('/messages', 'POST', { receiverId, subject, body });
        if (!res.ok) throw new Error('Failed to send message');
        return await res.json();
    },

    // Admin Marketplace
    getAdminMarketplaceAds: async (status?: string) => {
        const query = status ? `?status=${status}` : '';
        const res = await apiRequest(`/management/marketplace/ads${query}`);
        if (!res.ok) throw new Error('Failed to load admin ads');
        return await res.json();
    },

    adminUpdateAdStatus: async (id: string, status: string, reason?: string) => {
        const res = await apiRequest(`/management/marketplace/ads/${id}`, 'PUT', { status, reason });
        if (!res.ok) throw new Error('Failed to update status');
        return await res.json();
    },

    adminDeleteAd: async (id: string) => {
        const res = await apiRequest(`/management/marketplace/ads/${id}`, 'DELETE');
        if (!res.ok) throw new Error('Failed to delete ad');
        return await res.json();
    },

    // File Management
    getFiles: async () => {
        const res = await apiRequest('/management/files');
        if (!res.ok) throw new Error('Failed to load files');
        return await res.json();
    },

    getFile: async (id: string) => {
        const res = await apiRequest(`/management/files?id=${id}`);
        if (!res.ok) throw new Error('Failed to load file');
        return await res.json();
    },

    uploadFile: async (name: string, mime_type: string, data: string) => {
        const res = await apiRequest('/management/files', 'POST', { name, mime_type, data });
        if (!res.ok) {
            const errText = await res.text();
            throw new Error(`Upload failed (${res.status}): ${errText}`);
        }
        return await res.json();
    },

    deleteFile: async (id: string) => {
        const res = await apiRequest(`/management/files?id=${id}`, 'DELETE');
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(`Delete failed: ${JSON.stringify(body)}`);
        }
        return await res.json();
    },

    replaceFile: async (id: string, mime_type: string, data: string) => {
        const res = await apiRequest(`/management/files/${id}`, 'PUT', { mime_type, data });
        if (!res.ok) throw new Error('Replace failed');
        return await res.json();
    },

    deleteAllFiles: async () => {
        const res = await apiRequest(`/management/files?all=true`, 'DELETE');
        if (!res.ok) throw new Error('Delete all failed');
        return await res.json();
    },

    generateIcon: async (prompt: string, style: string) => {
        const res = await apiRequest('/ai/generate-icon', 'POST', { prompt, style });
        if (!res.ok) {
            let errorMsg = 'Generierung fehlgeschlagen';
            try {
                const body = await res.json();
                if (body && body.error) errorMsg = body.error;
                else if (body && typeof body === 'string') errorMsg = body;
            } catch (e) { }
            throw new Error(errorMsg);
        }
        return await res.json();
    },

    // Flight Comparison
    compareFlights: async (flightA: string, flightB: string, scoringProfile: string = 'balanced') => {
        const res = await apiRequest('/compareFlights', 'POST', {
            flight_a: flightA,
            flight_b: flightB,
            scoring_profile: scoringProfile
        });

        if (!res.ok) {
            // Forward the specific error from the backend (e.g. "Flights not found")
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || err.message || res.statusText || 'Comparison failed');
        }
        return await res.json();
    },

    // User Missions (Routes)
    getUserMissions: async (userId: string) => {
        try {
            const res = await apiRequest(`/user-missions?userId=${userId}`);
            if (res.ok) return await res.json();
            throw new Error(res.statusText);
        } catch (e) {
            console.warn("Backend unavailable for missions", e);
            return [];
        }
    },

    saveUserMission: async (mission: any) => {
        const res = await apiRequest('/user-missions', 'POST', mission);
        if (!res.ok) throw new Error('Failed to save mission');
        return await res.json();
    },

    deleteUserMission: async (id: string) => {
        const res = await apiRequest(`/user-missions?id=${id}`, 'DELETE');
        if (!res.ok) throw new Error('Failed to delete mission');
        return await res.json();
    },

    deleteRouteAnalysis: async (analysisId: string) => {
        const res = await apiRequest(`/analyses/${analysisId}`, 'DELETE');
        return handleResponse(res, 'Failed to delete analysis');
    },

    // --- DB Admin ---
    dbAdminGetTables: async () => {
        const res = await apiRequest('/db-admin/tables');
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },
    dbAdminGetSchema: async (table: string) => {
        const res = await apiRequest(`/db-admin/tables/${table}/schema`);
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },
    dbAdminGetData: async (table: string, limit = 100, offset = 0) => {
        const res = await apiRequest(`/db-admin/tables/${table}/data?limit=${limit}&offset=${offset}`);
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },
    dbAdminQuery: async (query: string) => {
        const res = await apiRequest(`/db-admin/query`, 'POST', { query });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || err.message || res.statusText || 'Query failed');
        }
        return await res.json();
    },
    dbAdminAddColumn: async (table: string, columnName: string, dataType: string, defaultValue?: string) => {
        const res = await apiRequest(`/db-admin/schema/${table}/add-column`, 'POST', { columnName, dataType, defaultValue });
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },
    dbAdminDropColumn: async (table: string, columnName: string) => {
        const res = await apiRequest(`/db-admin/schema/${table}/drop-column`, 'DELETE', { columnName });
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },

    // --- Task Matching & Soll-Ist-Vergleich (Milestone 3) ---
    getTaskMatchingStatus: async (flightId: string) => {
        const res = await apiRequest(`/task-matching/status/${flightId}`, 'GET');
        return handleResponse(res, 'Fehler beim Abrufen des Task-Matching-Status');
    },

    getTaskMatchingCandidates: async (flightId: string) => {
        const res = await apiRequest(`/task-matching/candidates/${flightId}`, 'GET');
        return handleResponse(res, 'Fehler beim Laden der Task-Matching-Kandidaten');
    },

    linkTask: async (data: { flightId?: string; trackId?: string; plannedTrackId?: string; plannedRouteId?: string; routeId?: string; matchType?: 'auto' | 'manual'; manual?: boolean; score?: number }) => {
        const res = await apiRequest('/task-matching/link', 'POST', data);
        return handleResponse(res, 'Fehler beim Verknüpfen der Aufgabe');
    },

    unlinkTask: async (flightId: string) => {
        const res = await apiRequest('/task-matching/unlink', 'POST', { flightId });
        return handleResponse(res, 'Fehler beim Lösen der Verknüpfung');
    },

    getTaskComparison: async (flightId: string, forceRecalculate = false, cylinderRadiusM = 400, gliderClass = 'EN-B') => {
        const params = new URLSearchParams({
            forceRecalculate: String(forceRecalculate),
            cylinderRadiusM: String(cylinderRadiusM),
            gliderClass
        });
        const res = await apiRequest(`/task-matching/compare/${flightId}?${params.toString()}`, 'GET');
        return handleResponse(res, 'Fehler beim Berechnen des Soll-Ist-Vergleichs');
    },

    generateTaskCoaching: async (flightId: string, forceRegenerate = false, pilotProfile?: any) => {
        const res = await apiRequest(`/task-matching/coach/${flightId}`, 'POST', {
            forceRegenerate,
            pilotProfile
        });
        return handleResponse(res, 'Fehler beim Generieren des KI-Coachings');
    },

    apiRequest: apiRequest
};

